import { Controller } from "@hotwired/stimulus"
import jquery from "jquery";

const $ = jquery

export default class extends Controller {
  static values = {
    isExpanded: { type: Boolean, default: false },
    wasExpanded: { type: Boolean, default: false },
    removed: { type: Boolean, default: false },
    focus: { type: Boolean, default: false },
    toggleCollapsedClass: { type: String, default: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-slate-600 dark:text-slate-100 dark:hover:text-slate-300 hover:bg-primary-200 dark:hover:bg-slate-500' },
    toggleExpandedClass: { type: String, default: 'hidden' },

    fieldsCollapsedClass: { type: String, default: 'hidden' },
    fieldsExpandedClass: { type: String, default: 'block' },

    // Text values that should be provided via i18n
    expandText: { type: String, default: 'Add' },
    collapseText: { type: String, default: 'Collapse' },
    removingText: { type: String, default: 'Removing' }
  }

  static targets = ["fields", "toggle", "collapsedOnly"]

  initialize() {
    this.boundCollapse = this.collapse.bind(this)
  }

  connect() {
    this._connected = false
    this.setup()
    this._connected = true

    // Store the original expanded state
    this.wasExpandedValue = this.isExpandedValue

    if (this.focusValue && this.hasFieldsTarget) {
      this.deferredFocus()
      // Only block collapse during init (timeline clearDate on connect)
      setTimeout(() => { this.focusValue = false }, 1000)
    }

    // Listen for a single generic event from any field controller
    this.element.addEventListener('expandable-field:cleared', this.boundCollapse)
  }

  disconnect() {
    // Clean up event listener
    this.element.removeEventListener('expandable-field:cleared', this.boundCollapse)
  }

  setup() {
    if (!this.hasToggleTarget) {
      this.addToggle()
    }

    this.applyState();
  }

  addToggle() {
    const toggleItem = document.createElement('span')

    const toggleLink = document.createElement('a')
    toggleLink.href = '#'
    toggleLink.dataset.action = 'click->expandable-fields#toggle'
    toggleLink.dataset.expandableFieldsTarget = 'toggle'
    toggleLink.className = 'no-underline cursor-pointer hover:no-underline'

    toggleItem.appendChild(toggleLink)
    this.element.appendChild(toggleItem)
  }

  applyState() {
    const $toggle = jquery(this.toggleTarget)

    // Check if this field is marked for deletion
    const destroyField = this.element.querySelector('input[name$="[_destroy]"]')
    const idField = this.element.querySelector('input[name$="[id]"]')
    const isPendingDelete = destroyField && destroyField.value === "1"
    const hasId = idField && idField.value && idField.value.trim() !== ""
    const shouldSubmitClearedValues = this.removedValue && !isPendingDelete && !hasId

    // If the field was originally expanded (had a value) but is now collapsed,
    // and it's not already marked for deletion, mark it for deletion
    if (!this.isExpandedValue && this.wasExpandedValue && !isPendingDelete && hasId && destroyField) {
      destroyField.value = "1"
      destroyField.disabled = false
    }

    if (this.isExpandedValue) {
      $toggle.text(this.collapseTextValue)
      $toggle.removeClass(this.toggleCollapsedClassValue)
      $toggle.addClass(this.toggleExpandedClassValue)
      $toggle.removeClass('text-red-600 dark:text-red-400')
      this.collapsedOnlyTargets.forEach((target) => target.classList.add('hidden'))
    } else {
      // If marked for deletion, show the removing text
      if ((isPendingDelete && hasId) || (!this.isExpandedValue && this.wasExpandedValue && hasId) || shouldSubmitClearedValues) {
        $toggle.text(this.removingTextValue)
        $toggle.addClass('text-red-600 dark:text-red-400')
      } else {
        $toggle.text(this.expandTextValue)
        $toggle.removeClass('text-red-600 dark:text-red-400')
      }

      $toggle.removeClass(this.toggleExpandedClassValue)
      $toggle.addClass(this.toggleCollapsedClassValue)
      this.collapsedOnlyTargets.forEach((target) => target.classList.remove('hidden'))
    }

    // Update fields classes and handle input states
    if (this.hasFieldsTarget) {
      const $fields = jquery(this.fieldsTarget)

      // Get all inputs except _destroy field
      const $inputs = $fields.find('input, select, textarea').not('[name$="[_destroy]"]')

      if (this.isExpandedValue) {
        $fields.removeClass(this.fieldsCollapsedClassValue)
        $fields.addClass(this.fieldsExpandedClassValue)

        // Re-enable all form elements and restore their previous disabled state
        $inputs.each(function() {
          const wasDisabled = $(this).data('was-disabled')
          if (wasDisabled !== true) {
            $(this).prop('disabled', false)
          }
        })

        if (this._connected) this.focusFirstField($fields)
      } else {
        $fields.removeClass(this.fieldsExpandedClassValue)
        $fields.addClass(this.fieldsCollapsedClassValue)

        // Store current disabled state and disable all form elements
        $inputs.each(function() {
          const $input = $(this)
          $input.data('was-disabled', $input.prop('disabled'))

          // If this is an ID field and the record is marked for deletion, don't disable it
          const isIdField = $input.attr('name') && $input.attr('name').endsWith('[id]')
          if (isIdField && (isPendingDelete || (!this.isExpandedValue && this.wasExpandedValue && hasId))) {
            $input.prop('disabled', false)
          } else if (shouldSubmitClearedValues) {
            $input.prop('disabled', false)
          } else {
            $input.prop('disabled', true)
          }
        })
      }
    }
  }

  toggle(event) {
    event.preventDefault()  // Prevent the default link behavior
    const nextExpandedState = !this.isExpandedValue

    if (nextExpandedState) {
      this.removedValue = false
      if (this.destroyField) {
        this.destroyField.value = "0"
      }
    }

    this.isExpandedValue = nextExpandedState

    this.applyState()
  }

  remove(event) {
    event?.preventDefault()

    const destroyField = this.destroyField
    const hasPersistedNestedRecord = this.hasPersistedNestedRecord

    if (hasPersistedNestedRecord && destroyField) {
      destroyField.value = "1"
      destroyField.disabled = false
    }

    this.removedValue = this.wasExpandedValue && !hasPersistedNestedRecord
    this.clearFields({preserveId: hasPersistedNestedRecord})

    if (this.isExpandedValue) {
      this.isExpandedValue = false
    }

    this.applyState()
  }

  collapse(event) {
    if (this.focusValue) return

    if (this.isExpandedValue) {
      this.isExpandedValue = false
      this.applyState()
    }
  }

  deferredFocus() {
    const $fields = jquery(this.fieldsTarget)

    // Single delayed focus — late enough for modal focus-traps to settle.
    // On direct visits the timeline controller opens the picker via plugin.show()
    // when it detects its input already has focus, so this only matters in modals.
    setTimeout(() => {
      this.focusFirstField($fields)
    }, 500)
  }

  focusFirstField($fields = jquery(this.fieldsTarget)) {
    const field = $fields.find('input:visible:first, textarea:visible:first, select:visible:first, .select2-selection:visible:first')[0]
    if (field && document.activeElement !== field) field.focus()
  }

  clearFields({preserveId = false} = {}) {
    if (!this.hasFieldsTarget) return

    this.fieldsTarget.querySelectorAll("input, select, textarea").forEach((input) => {
      if (input.name?.endsWith("[_destroy]")) return
      if (preserveId && input.name?.endsWith("[id]")) {
        input.disabled = false
        return
      }

      if (input.tagName === "SELECT") {
        if (input.multiple) {
          Array.from(input.options).forEach((option) => {
            option.selected = false
          })
        } else {
          input.value = ""
        }
      } else if (input.type === "checkbox" || input.type === "radio") {
        input.checked = false
      } else {
        input.value = ""
      }

      input.dispatchEvent(new Event("change", {bubbles: true}))
    })
  }

  get destroyField() {
    return this.element.querySelector('input[name$="[_destroy]"]')
  }

  get idField() {
    return this.element.querySelector('input[name$="[id]"]')
  }

  get hasPersistedNestedRecord() {
    return Boolean(this.destroyField && this.idField?.value?.trim())
  }
}
